import {
  Banknote,
  CheckCircle2,
  CircleAlert,
  CircleCheck,
  CircleHelp,
  ClipboardCheck,
  Clock,
  Loader,
  Minimize2,
  PauseCircle,
  ReceiptText,
  RotateCcw,
  ShieldAlert,
  Timer,
  Wrench,
  XCircle,
} from 'lucide-react'
import { useEffect, useId, useRef, useState } from 'react'

import { CANCEL_QUEUED_TEXT, trace, type TraceStage } from '@krp/client-runtime'
import { Button, DiagnosticCopy, SelectField, TextField } from '@krp/ui-kit'

import { SALE_DONE_FLASH_MS } from '../limits'
import type {
  ParkActions,
  ParkVm,
  PaymentDoneVm,
  PaymentFlowVm,
  PaymentNoticeVm,
  PaymentResolveActions,
  PaymentResolveVm,
  ProgressStepState,
  ProgressToolActions,
  ProgressToolVm,
  ReceiptRetryActions,
  ReceiptRetryVm,
  SaleActions,
  SaleVm,
} from '../port/types'
import { LatePaymentChoice } from './LatePaymentChoice'
import { ReasonField } from './ReasonField'
import { ReceiptVerdict } from './ReceiptVerdict'
import { RecoveryPanel } from './RecoveryPanel'
import { RmkDialog } from './RmkDialog'
import { useElapsed } from './useElapsed'

/** Этапы, где идёт сам ход оплаты или чека: окно говорит «Отменяем…», пока «Отменить» в пути. */
const RUNNING_STAGES: ReadonlySet<PaymentFlowVm['stage']> = new Set(['saving', 'preparing', 'terminal', 'checking'])

/** Какой этап окна оплаты — какая контрольная точка трассировки продажи (RP-SALE-TRACE). Остальные этапы точек не дают. */
const STAGE_TRACE: Readonly<Partial<Record<PaymentFlowVm['stage'], TraceStage>>> = {
  terminal: 'terminal-shown',
  receipt: 'receipt-shown',
  done: 'done',
}

/**
 * Окно оплаты (RP-PAY-ONE, решение владельца 23.10.2026): ОДНО окно на попытку оплаты — от «Картой» / «Наличными» / «Повторить оплату» до
 * итога. Этап меняет содержимое, а не окно: оно не закрывается и не открывается заново, размер один. Крестика нет: «Свернуть» уводит ход в
 * блок состояния («Ход оплаты: …»), сам он не разворачивается. Подтверждения «Пробить чек?» и «Сверить журнал?», ручная сверка и итог —
 * внутри окна. Ход, отказ и «ответ не получен» команд оплаты — здесь, внизу их нет. Экран ничего не решает: этап и кнопки даёт источник.
 */
export function PaymentWindow({ payment, vm, actions }: { payment: PaymentFlowVm; vm: SaleVm; actions: SaleActions }) {
  const [confirmation, setConfirmation] = useState<'fiscalize' | 'sync' | null>(null)
  const elapsed = useElapsed(payment.waitingSince)
  const tracedSale = useRef<string | null>(null)
  const tracedStage = useRef<string | null>(null)
  const { stage: shownStage, attemptKey } = payment
  const { saleId: shownSale, resourceId: shownScope } = vm
  // Окно показало этап — контрольная точка продажи; один этап одной попытки отмечается один раз.
  useEffect(() => {
    if (shownSale !== null) tracedSale.current = shownSale
    const point = STAGE_TRACE[shownStage]
    const mark = `${attemptKey}:${shownStage}`
    if (point === undefined || tracedSale.current === null || tracedStage.current === mark) return
    tracedStage.current = mark
    trace.mark(point, { scope: shownScope, saleId: tracedSale.current, attemptKey })
  }, [shownStage, attemptKey, shownSale, shownScope])
  const outside = payment.stage === 'no-receipt' ? vm.outside : null
  const retry = payment.retry
  const park = payment.park
  const stage = payment.stage
  const cancellingText = stage === 'terminal' ? 'Отменяем оплату на терминале…' : 'Проверяем оплату на кассе и в кабинете…'
  // Чек не прошёл или завис: деньги взяты, а чека нет (или он стоит).
  const receiptTrouble = stage === 'no-receipt' || (stage === 'receipt' && payment.tone !== 'neutral')
  const repeat = receiptTrouble && retry != null && retry.form === null && actions.receiptRetry !== undefined ? retry : null
  const retryForm = receiptTrouble && retry != null && retry.form !== null
  // Отмена в очереди (связи нет): кнопка остаётся на месте, занятая, пока источник не отправит команду (RP-SALE-FAST, шаг 5).
  const cancelQueued = payment.cancelQueued === true
  const main: 'cancel' | 'repeat' | 'fiscalize' | null = payment.canCancelPayment || cancelQueued
    ? 'cancel'
    : repeat !== null
      ? 'repeat'
      : payment.canFiscalize && !retryForm
        ? 'fiscalize'
        : null
  const problem = receiptTrouble || payment.manualRequired || stage === 'checking' || stage === 'declined' || stage === 'not-started'
  // Почему «Отменить» нет — под «Подробнее» в любом этапе хода, а не только при проблеме: кассир ищет кнопку, пока терминал ждёт карту.
  const cancelNote = !payment.canCancelPayment && RUNNING_STAGES.has(stage) ? (payment.cancelUnavailable ?? null) : null
  const lines = [...(problem ? payment.details : []), ...(cancelNote === null ? [] : [cancelNote])]
  const tools = actions.tool === undefined ? [] : (payment.tools ?? [])
  const warnings = payment.warnings ?? []
  const hasMore = lines.length > 0 || payment.canMarkOutside || tools.length > 0
  const confirming =
    (confirmation === 'fiscalize' && main === 'fiscalize') ||
    (confirmation === 'sync' && outside?.offerJournalSync === true && actions.syncReceiptJournal !== undefined)
  const cancelling = payment.cancelling && RUNNING_STAGES.has(stage)
  const text = cancelling && !cancelQueued ? cancellingText : payment.text
  // Панель «Проверка в …» (RP-CASH-RECOVERY §4.1): источник сам читает исход чека и даёт одно действие — своё копирование «для разбора».
  const recovery = payment.recovery != null && actions.recovery !== undefined ? payment.recovery : null

  return (
    <RmkDialog title={payment.title} size="md" watchAge={payment.waiting}>
      <div className="rmk-progress" role="region" aria-label="Ход оплаты" data-stage={stage} data-tone={payment.tone}>
        {stage === 'done' && payment.done !== null ? (
          <PaymentDone done={payment.done} amountText={payment.amountText} warnings={warnings} onDone={actions.startNewSale} />
        ) : (
          <>
            <div className="rmk-progress__head" role="status" aria-live="assertive">
              <div>
                <p className="rmk-progress__amount" data-big={stage === 'terminal' || stage === 'preparing' || undefined}>
                  {payment.amountText}
                </p>
                {text.split('\n').map((line) => (
                  <p key={line} className="rmk-progress__text" data-big={stage === 'terminal' || undefined}>
                    {line}
                  </p>
                ))}
                {elapsed !== null && (
                  <p className="rmk-progress__elapsed" aria-label="Прошло времени">
                    <Timer size={14} aria-hidden="true" /> {elapsed}
                  </p>
                )}
              </div>
            </div>

            {payment.notice !== null && <PaymentNotice notice={payment.notice} />}

            {recovery === null && (problem || payment.tone === 'danger' || payment.tone === 'warning' || warnings.length > 0 || payment.notice !== null) && (
              <DiagnosticCopy
                text={[
                  payment.title,
                  payment.amountText,
                  payment.text,
                  ...payment.steps.map((step) => `${step.label}: ${step.text}`),
                  ...warnings,
                  ...(payment.notice === null ? [] : [payment.notice.text, payment.notice.meta ?? '']),
                  ...payment.details,
                ]
                  .filter((line) => line !== '')
                  .join('\n')}
              />
            )}

            {warnings.length > 0 && (
              <ul className="rmk-progress__warnings" aria-label="Предупреждения">
                {warnings.map((warning) => (
                  <li key={warning}>
                    <CircleAlert size={18} aria-hidden="true" /> <strong>Внимание.</strong> {warning}
                  </li>
                ))}
              </ul>
            )}

            <ol className="rmk-progress__steps" aria-label="Шаги проведения">
              {payment.steps.map((step) => (
                <li key={step.key} className="rmk-progress__step" data-state={step.state}>
                  <StepIcon state={step.state} />
                  <div>
                    <p className="rmk-progress__step-title">
                      <span>{step.label}</span> — <strong>{step.text}</strong>
                    </p>
                    {step.key === 'receipt' && payment.watchNote != null && !cancelling && (
                      <p className="rmk-progress__watch" aria-label="Проверки">
                        {payment.watchNote}
                      </p>
                    )}
                  </div>
                </li>
              ))}
            </ol>

            {recovery !== null && actions.recovery !== undefined && <RecoveryPanel recovery={recovery} actions={actions.recovery} />}

            {stage === 'manual' && payment.resolve !== null && actions.resolve !== undefined && (
              <PaymentResolve resolve={payment.resolve} actions={actions.resolve} />
            )}

            {stage === 'late-slip' && payment.latePayment !== null && actions.resolveLatePayment !== undefined && (
              <LatePaymentChoice latePayment={payment.latePayment} onResolve={actions.resolveLatePayment} />
            )}

            {confirming && (
              <section className="rmk-progress__confirm" aria-label={confirmation === 'fiscalize' ? 'Пробить чек?' : 'Сверить журнал чеков?'}>
                <h3>{confirmation === 'fiscalize' ? 'Пробить чек?' : 'Сверить журнал чеков?'}</h3>
                <p className="sale-text">
                  {confirmation === 'fiscalize'
                    ? 'На кассу будет отправлена команда формирования фискального чека этой продажи. Продолжить?'
                    : 'Служба запросит обновление журнала чеков из кабинета. Затем можно повторить привязку существующего чека.'}
                </p>
                <div className="rmk-progress__actions">
                  <Button
                    variant="primary"
                    onClick={() => {
                      setConfirmation(null)
                      if (confirmation === 'fiscalize') actions.fiscalize()
                      else actions.syncReceiptJournal?.()
                    }}
                  >
                    Подтвердить
                  </Button>
                  <Button variant="ghost" onClick={() => setConfirmation(null)}>
                    Отмена
                  </Button>
                </div>
              </section>
            )}

            {!confirming && outside !== null && (
              <form
                className="rmk-progress__outside sale-form"
                aria-label="Привязать уже пробитый чек"
                onSubmit={(event) => {
                  event.preventDefault()
                  actions.submitIssuedOutside()
                }}
              >
                <h3>Привязать существующий фискальный чек к этой продаже?</h3>
                <p className="sale-text">
                  Используйте это действие, если фискальный чек этой продажи уже сформирован, например вручную на кассе вне нашей программы.
                  Служба проверит чек в журнале чеков и закроет им продажу. Новый чек, в том числе чек коррекции, это действие не создаёт.
                </p>
                <ReasonField label="Причина (обязательно)" value={outside.reason} onChange={actions.outsideReasonChanged} />
                <TextField label="Номер чека кассы (если подходит несколько)" value={outside.receiptId} onChange={actions.outsideReceiptChanged} />
                {outside.hint !== null && <p className="sale-text">{outside.hint}</p>}
                {outside.submitBlockedReason !== null && <p className="sale-text">{outside.submitBlockedReason}</p>}
                <div className="rmk-progress__actions">
                  <Button type="submit" variant="primary" disabled={!outside.canSubmit} busy={outside.sending}>
                    Подтвердить привязку
                  </Button>
                  {outside.offerJournalSync && actions.syncReceiptJournal !== undefined && (
                    <Button onClick={() => setConfirmation('sync')}>Сверить журнал чеков</Button>
                  )}
                  <Button variant="ghost" disabled={outside.sending} onClick={actions.closeIssuedOutside}>
                    Отмена
                  </Button>
                </div>
              </form>
            )}

            {!confirming && outside === null && main !== null && (
              <div className="rmk-progress__main">
                {main === 'cancel' && (
                  <Button
                    variant="danger"
                    icon={<XCircle size={22} aria-hidden="true" />}
                    busy={cancelling || cancelQueued}
                    disabled={cancelling || cancelQueued}
                    onClick={actions.cancelPayment}
                  >
                    Отменить
                  </Button>
                )}
                {main === 'cancel' && cancelQueued && (
                  <p className="sale-text" role="status">
                    <Clock size={14} aria-hidden="true" /> {CANCEL_QUEUED_TEXT}
                  </p>
                )}
                {main === 'repeat' && repeat !== null && (
                  <>
                    <Button
                      variant="primary"
                      icon={repeat.canOpen ? <RotateCcw size={22} aria-hidden="true" /> : <Clock size={22} aria-hidden="true" />}
                      disabled={!repeat.canOpen}
                      onClick={actions.receiptRetry?.repeat ?? actions.receiptRetry?.open}
                    >
                      {repeat.label}
                    </Button>
                    {repeat.blockedText !== null && (
                      <p className="sale-text" role="status">
                        {repeat.blockedText}
                      </p>
                    )}
                  </>
                )}
                {main === 'fiscalize' && (
                  <Button variant="primary" icon={<ReceiptText size={22} aria-hidden="true" />} onClick={() => setConfirmation('fiscalize')}>
                    Пробить чек
                  </Button>
                )}
              </div>
            )}

            {!confirming && outside === null && payment.canCancelPreparation === true && actions.cancelPreparation !== undefined && (
              // До денег «Отменить» безопасна: оплата ещё не отправлена, попытка закрывается, корзина остаётся как есть.
              <div className="rmk-progress__main">
                <Button variant="danger" icon={<XCircle size={22} aria-hidden="true" />} onClick={actions.cancelPreparation}>
                  Отменить
                </Button>
              </div>
            )}

            {!confirming && (payment.repeat !== null || payment.alternative !== null || payment.canClose) && (
              <div className="rmk-progress__main">
                {payment.repeat !== null && (
                  <Button
                    variant="primary"
                    icon={<RotateCcw size={22} aria-hidden="true" />}
                    disabled={!payment.repeat.enabled}
                    onClick={() => {
                      if (payment.repeat !== null) actions.payWith(payment.repeat.kind)
                    }}
                  >
                    {payment.repeat.label}
                  </Button>
                )}
                {payment.repeat?.blockedReason != null && (
                  <p className="sale-text" role="status">
                    <Clock size={14} aria-hidden="true" /> {payment.repeat.blockedReason}
                  </p>
                )}
                {payment.alternative !== null && (
                  <Button
                    icon={<Banknote size={18} aria-hidden="true" />}
                    disabled={!payment.alternative.enabled}
                    onClick={() => {
                      if (payment.alternative !== null) actions.payWith(payment.alternative.kind)
                    }}
                  >
                    {payment.alternative.label}
                  </Button>
                )}
                {payment.canClose && (
                  <Button variant="ghost" onClick={actions.closePayment}>
                    Закрыть
                  </Button>
                )}
              </div>
            )}

            {!confirming && outside === null && stage !== 'manual' && park != null && actions.park !== undefined && (
              <ParkSale park={park} actions={actions.park} />
            )}

            {!confirming && outside === null && retryForm && retry != null && actions.receiptRetry !== undefined && (
              <ReceiptRetry retry={retry} actions={actions.receiptRetry} />
            )}

            {!confirming && outside === null && stage !== 'manual' && payment.manualRequired && (
              <div className="rmk-progress__secondary">
                <Button icon={<ShieldAlert size={16} aria-hidden="true" />} onClick={actions.openManualResolve}>
                  Ручная сверка
                </Button>
              </div>
            )}

            {!confirming && outside === null && stage !== 'manual' && hasMore && (
              <details className="rmk-progress__more" open={main === null && (payment.canMarkOutside || tools.length > 0) ? true : undefined}>
                <summary>Подробнее</summary>
                {lines.map((line) => (
                  <p key={line} className="rmk-progress__details-line">
                    {line}
                  </p>
                ))}
                <div className="rmk-progress__secondary">
                  {payment.canMarkOutside && (
                    <Button icon={<ClipboardCheck size={16} aria-hidden="true" />} onClick={actions.openIssuedOutside}>
                      Привязать уже пробитый чек
                    </Button>
                  )}
                  {actions.tool !== undefined && tools.map((tool) => <ProgressTool key={tool.key} tool={tool} actions={actions.tool as ProgressToolActions} />)}
                </div>
              </details>
            )}

            {payment.canCollapse && (
              <div className="rmk-progress__collapse">
                <Button variant="ghost" icon={<Minimize2 size={16} aria-hidden="true" />} onClick={actions.collapsePayment}>
                  Свернуть
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </RmkDialog>
  )
}

/** Ответ команды оплаты в окне: тон — значком и словом, а не только цветом; отказ дословно, код — под «Подробнее». */
function PaymentNotice({ notice }: { notice: PaymentNoticeVm }) {
  const Icon = notice.tone === 'danger' ? XCircle : notice.tone === 'warning' ? CircleHelp : CircleCheck
  return (
    <div className="rmk-progress__notice" data-tone={notice.tone} role={notice.tone === 'neutral' ? 'status' : 'alert'}>
      <p>
        <Icon size={16} aria-hidden="true" /> <strong>{notice.title}.</strong> {notice.text}
      </p>
      {notice.hint !== null && <p className="sale-text">{notice.hint}</p>}
      {notice.meta !== null && (
        <details>
          <summary>Подробнее</summary>
          <p className="rmk-progress__details-line">{notice.meta}</p>
        </details>
      )}
    </div>
  )
}

/**
 * Итог в окне оплаты: обычный успех — галочка на доли секунды и сразу новая продажа (без сообщения, которое надо закрывать); чек с
 * кассы или предупреждение службы — текст и «Понятно». Слово «Чек пробит» — и для диктора.
 */
function PaymentDone({ done, amountText, warnings, onDone }: { done: PaymentDoneVm; amountText: string; warnings: readonly string[]; onDone: () => void }) {
  const finish = useRef(onDone)
  useEffect(() => {
    finish.current = onDone
  }, [onDone])
  useEffect(() => {
    if (!done.autoClose) return undefined
    const timer = setTimeout(() => finish.current(), SALE_DONE_FLASH_MS)
    return () => clearTimeout(timer)
  }, [done.autoClose])
  return (
    <div className="rmk-progress__done" role="status" aria-live="polite" data-testid="rmk-done-flash">
      <CheckCircle2 size={done.autoClose ? 160 : 72} strokeWidth={1.5} aria-hidden="true" />
      {done.autoClose ? (
        <span className="visually-hidden">{done.announce ?? 'Чек пробит'}</span>
      ) : (
        <>
          <p className="rmk-progress__amount">{amountText}</p>
          <p className="rmk-progress__text">{done.text}</p>
          {warnings.map((warning) => (
            <p key={warning} className="rmk-progress__text" role="alert">
              <CircleAlert size={16} aria-hidden="true" /> {warning}
            </p>
          ))}
          <div className="rmk-progress__main">
            <Button variant="primary" onClick={onDone}>
              Понятно
            </Button>
          </div>
        </>
      )}
    </div>
  )
}

/**
 * Ручная сверка внутри окна оплаты (решение владельца 23.10.2026, вопрос 2): человек проверяет терминал, кассу и кабинет и записывает итог
 * с причиной. Кто сверял — служба берёт из токена; повторить оплату отсюда нельзя — такой кнопки нет. Сверка чека — две галочки и одно
 * действие (`resolve.verdict`, решение владельца 04.11.2026) вместо итога и свободного текста.
 */
function PaymentResolve({ resolve, actions }: { resolve: PaymentResolveVm; actions: PaymentResolveActions }) {
  const name = useId()
  const locked = !resolve.allowed || resolve.sending || resolve.done
  const verdict = resolve.verdict
  return (
    <section className="rmk-progress__resolve" aria-label="Ручная сверка">
      <h3>
        <ShieldAlert size={16} aria-hidden="true" /> {resolve.kindText}: {resolve.stateText}
      </h3>
      {resolve.details.map((line) => (
        <p key={line} className="rmk-progress__details-line">
          {line}
        </p>
      ))}
      {resolve.allowedReason !== null && <p className="sale-text">{resolve.allowedReason}</p>}
      {resolve.allowed && verdict != null && actions.verdict !== undefined && (
        <>
          <ReceiptVerdict verdict={verdict} actions={actions.verdict} />
          <div className="rmk-progress__actions">
            <Button variant="ghost" disabled={verdict.busy} onClick={actions.close}>
              Назад
            </Button>
          </div>
        </>
      )}
      {resolve.allowed && (verdict == null || actions.verdict === undefined) && (
        <form
          className="sale-form"
          aria-label="Итог сверки"
          onSubmit={(event) => {
            event.preventDefault()
            actions.submit()
          }}
        >
          <fieldset className="sale__kind" disabled={locked}>
            <legend>Что показала проверка</legend>
            <label>
              <input type="radio" name={name} checked={resolve.outcome === 'Succeeded'} onChange={() => actions.outcomeChanged('Succeeded')} />
              <CheckCircle2 size={14} aria-hidden="true" /> Операция исполнена
            </label>
            <label>
              <input type="radio" name={name} checked={resolve.outcome === 'Failed'} onChange={() => actions.outcomeChanged('Failed')} />
              <XCircle size={14} aria-hidden="true" /> Не исполнена — денег/чека нет
            </label>
          </fieldset>
          <ReasonField label="Причина" value={resolve.reason} onChange={actions.reasonChanged} />
          <ReasonField label="Что проверено на кассе, терминале или в кабинете" value={resolve.checked} onChange={actions.checkedChanged} />
          <TextField
            label={resolve.resultIdRequired ? 'Номер слипа (для исполненного списания — обязательно)' : 'Номер чека (если виден)'}
            value={resolve.resultId}
            onChange={actions.resultIdChanged}
          />
          {resolve.submitBlockedReason !== null && <p className="sale-text">{resolve.submitBlockedReason}</p>}
          <div className="rmk-progress__actions">
            <Button type="submit" variant="primary" disabled={!resolve.canSubmit} busy={resolve.sending}>
              Записать
            </Button>
            <Button variant="ghost" disabled={resolve.sending} onClick={actions.close}>
              Назад
            </Button>
          </div>
        </form>
      )}
      {!resolve.allowed && (
        <div className="rmk-progress__actions">
          <Button variant="ghost" onClick={actions.close}>
            Назад
          </Button>
        </div>
      )}
    </section>
  )
}

/** Иконка шага: галочка, крутилка, часы, крестик, вопрос — состояние читается и без цвета. */
function StepIcon({ state }: { state: ProgressStepState }) {
  switch (state) {
    case 'done':
      return <CircleCheck className="rmk-progress__step-icon" size={26} aria-hidden="true" />
    case 'active':
      return <Loader className="rmk-progress__step-icon rmk-progress__spinner" size={26} aria-hidden="true" />
    case 'failed':
      return <XCircle className="rmk-progress__step-icon" size={26} aria-hidden="true" />
    case 'unknown':
      return <CircleHelp className="rmk-progress__step-icon" size={26} aria-hidden="true" />
    default:
      return <Clock className="rmk-progress__step-icon" size={26} aria-hidden="true" />
  }
}

/**
 * «Чека нет — пробить заново»: кнопка (недоступная до срока — с иконкой часов и словами «Доступно через N мин»), затем форма «Что
 * проверили» и «Причина». Отправка — одна команда на нажатие, повтор гасит источник.
 */
function ReceiptRetry({ retry, actions }: { retry: ReceiptRetryVm; actions: ReceiptRetryActions }) {
  const name = useId()
  const form = retry.form
  if (form === null) {
    return (
      <div className="rmk-progress__retry">
        <Button
          icon={retry.canOpen ? <RotateCcw size={16} aria-hidden="true" /> : <Clock size={16} aria-hidden="true" />}
          disabled={!retry.canOpen}
          onClick={actions.open}
        >
          {retry.label}
        </Button>
        {retry.blockedText !== null && (
          <p className="sale-text" role="status">
            {retry.blockedText}
          </p>
        )}
      </div>
    )
  }
  const other = form.options.find((option) => option.value === form.checked)?.needsText === true
  return (
    <form
      className="rmk-progress__retry sale-form"
      aria-label={retry.label}
      onSubmit={(event) => {
        event.preventDefault()
        actions.submit()
      }}
    >
      <fieldset className="sale__kind" disabled={form.sending}>
        <legend>Что проверили</legend>
        {form.options.map((option) => (
          <label key={option.value}>
            <input type="radio" name={name} checked={form.checked === option.value} onChange={() => actions.checkedChanged(option.value)} />{' '}
            {option.label}
          </label>
        ))}
      </fieldset>
      {other && <TextField label="Что именно проверили" value={form.checkedText} onChange={actions.checkedTextChanged} />}
      <ReasonField label="Причина (обязательно)" value={form.reason} onChange={actions.reasonChanged} />
      {form.hint !== null && <p className="sale-text">{form.hint}</p>}
      {form.submitBlockedReason !== null && <p className="sale-text">{form.submitBlockedReason}</p>}
      <div className="rmk-progress__actions">
        <Button type="submit" variant="primary" icon={<RotateCcw size={16} aria-hidden="true" />} disabled={!form.canSubmit} busy={form.sending}>
          Пробить заново
        </Button>
        <Button variant="ghost" disabled={form.sending} onClick={actions.close}>
          Отмена
        </Button>
      </div>
    </form>
  )
}

/** «Отложить продажу»: кнопка, затем подтверждение с необязательной причиной. Отправка — одна команда на нажатие. */
function ParkSale({ park, actions }: { park: ParkVm; actions: ParkActions }) {
  const form = park.form
  if (form === null) {
    return (
      <div className="rmk-progress__park">
        <Button icon={park.canOpen ? <PauseCircle size={16} aria-hidden="true" /> : <Clock size={16} aria-hidden="true" />} disabled={!park.canOpen} onClick={actions.open}>
          {park.label}
        </Button>
        {park.blockedText !== null && (
          <p className="sale-text" role="status">
            {park.blockedText}
          </p>
        )}
      </div>
    )
  }
  return (
    <form
      className="rmk-progress__park sale-form"
      aria-label={park.label}
      onSubmit={(event) => {
        event.preventDefault()
        actions.submit()
      }}
    >
      <h3>{park.label}?</h3>
      <p className="sale-text">{form.confirmText}</p>
      {form.reasonVisible !== false && <ReasonField label="Причина (необязательно)" value={form.reason} onChange={actions.reasonChanged} />}
      {form.hint !== null && <p className="sale-text">{form.hint}</p>}
      {form.submitBlockedReason !== null && <p className="sale-text">{form.submitBlockedReason}</p>}
      <div className="rmk-progress__actions">
        <Button type="submit" variant="primary" icon={<PauseCircle size={16} aria-hidden="true" />} disabled={!form.canSubmit} busy={form.sending}>
          Подтвердить откладывание
        </Button>
        <Button variant="ghost" disabled={form.sending} onClick={actions.close}>
          Отмена
        </Button>
      </div>
    </form>
  )
}

/** Действие источника под «Подробнее»: кнопка (одно нажатие или с формой). Недоступное - с иконкой часов и словами, почему. */
function ProgressTool({ tool, actions }: { tool: ProgressToolVm; actions: ProgressToolActions }) {
  const [confirming, setConfirming] = useState(false)
  const form = tool.form
  if (form === null && tool.direct === true && confirming) {
    return (
      <form
        className="rmk-progress__tool sale-form"
        aria-label={tool.label}
        onSubmit={(event) => {
          event.preventDefault()
          if (!tool.canOpen || tool.busy) return
          setConfirming(false)
          actions.run(tool.key)
        }}
      >
        <p className="sale-text">Выполнить действие «{tool.label}» для этой продажи?</p>
        <div className="rmk-progress__actions">
          <Button type="submit" variant="primary" disabled={!tool.canOpen || tool.busy}>
            Подтвердить
          </Button>
          <Button variant="ghost" onClick={() => setConfirming(false)}>
            Отмена
          </Button>
        </div>
      </form>
    )
  }
  if (form === null) {
    return (
      <div className="rmk-progress__tool">
        <Button
          icon={tool.canOpen ? <Wrench size={16} aria-hidden="true" /> : <Clock size={16} aria-hidden="true" />}
          disabled={!tool.canOpen}
          busy={tool.busy}
          onClick={() => (tool.direct === true ? setConfirming(true) : actions.open(tool.key))}
        >
          {tool.label}
        </Button>
        {tool.blockedText !== null && (
          <p className="sale-text" role="status">
            {tool.blockedText}
          </p>
        )}
      </div>
    )
  }
  return (
    <form
      className="rmk-progress__tool sale-form"
      aria-label={tool.label}
      data-danger={form.danger || undefined}
      onSubmit={(event) => {
        event.preventDefault()
        actions.submit(tool.key)
      }}
    >
      <p className="sale-text">
        {form.danger && <CircleAlert size={16} aria-hidden="true" />} {form.text}
      </p>
      {form.fields.map((field) =>
        field.kind === 'select' ? (
          <SelectField
            key={field.key}
            label={field.label}
            value={field.value}
            options={field.options ?? []}
            onChange={(value) => actions.fieldChanged(tool.key, field.key, value)}
          />
        ) : (
          <div key={field.key}>
            <TextField label={field.required ? `${field.label} (обязательно)` : field.label} value={field.value} onChange={(value) => actions.fieldChanged(tool.key, field.key, value)} />
            {field.hint != null && <p className="sale-text">{field.hint}</p>}
            {field.error !== null && (
              <p className="sale-text" role="alert">
                {field.error}
              </p>
            )}
          </div>
        ),
      )}
      {form.hint !== null && <p className="sale-text">{form.hint}</p>}
      {form.submitBlockedReason !== null && <p className="sale-text">{form.submitBlockedReason}</p>}
      <div className="rmk-progress__actions">
        <Button type="submit" variant={form.danger ? 'danger' : 'primary'} disabled={!form.canSubmit} busy={form.sending}>
          {form.submitLabel}
        </Button>
        <Button variant="ghost" disabled={form.sending} onClick={() => actions.close(tool.key)}>
          Отмена
        </Button>
      </div>
    </form>
  )
}
